import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  BROWSER_TENANT_STORAGE_KEY,
  BROWSER_TENANT_CHANGED_EVENT,
  getCurrentUser,
  getPinnedSkillWorkspaces,
  SKILL_WORKSPACES_CHANGED_EVENT,
} from "../lib/api";
import { useTheme } from "./ThemeProvider";
import FloatingMic from "./FloatingMic";
import { isSupportTab, endSupportTab, supportRequest } from '../lib/supportSession';
import { useNativeSupportBootstrap } from '../lib/nativeSupportBootstrap';
import {
  SquaresFour, Circuitry, Robot, Lightning, Clock, Newspaper, Pulse,
  Database, Plugs, Storefront, ChartBar, Gear, ShieldCheck,
  List, X, SignOut, Sun, Moon, UserCircle,
  CaretLeft, CaretRight, Code, Eye, PuzzlePiece, WarningCircle, Waveform,
  Briefcase, Sparkle, DesktopTower, ListChecks, ChatsCircle, FileText, Gavel, BookOpenText
} from "@phosphor-icons/react";

const operateNavItems = [
  { id: "today", path: "/", icon: Waveform, label: "Today" },
  { id: "entities", path: "/entities", icon: Database, label: "Entities" },
  { id: "skill-workspaces", path: "/skill-workspaces", icon: Sparkle, label: "Skill workspaces" },
  { id: "forms", path: "/forms", icon: FileText, label: "Forms" },
  { id: "functions", path: "/functions", icon: Briefcase, label: "Functions" },
  { id: "briefings", path: "/briefings", icon: Newspaper, label: "Briefings" },
  { id: "activity", path: "/activity", icon: Pulse, label: "Activity" },
  { id: "canvases", path: "/canvases", icon: SquaresFour, label: "Canvases" },
];

const manageNavItems = [
  { id: "dashboard", path: "/manage/dashboard", icon: SquaresFour, label: "System overview" },
  { id: "coverage", path: "/manage/coverage", icon: WarningCircle, label: "Setup & coverage" },
  { id: "orchestrator", path: "/orchestrator", icon: Circuitry, label: "Orchestrator" },
  { id: "sops", path: "/sops", icon: BookOpenText, label: "SOPs & Workflows" },
  { id: "agents", path: "/agents", icon: Robot, label: "Agents" },
  { id: "skills", path: "/skills", icon: PuzzlePiece, label: "Roles & Skills" },
  { id: "actions", path: "/actions", icon: Lightning, label: "Events & Actions" },
  { id: "monitors", path: "/monitors", icon: Eye, label: "Rule Monitors" },
  { id: "automations", path: "/automations", icon: Clock, label: "Automation Ops" },
  { id: "continuations", path: "/continuations", icon: Clock, label: "Event waits" },
  { id: "policies", path: "/policies", icon: Gavel, label: "Policies" },
  { id: "conversations", path: "/conversations", icon: ChatsCircle, label: "Conversations" },
  { id: "obligations", path: "/obligations", icon: ListChecks, label: "Business Obligations" },
  { id: "documents", path: "/documents", icon: FileText, label: "Document Studio" },
  { id: "entity-cache", path: "/entity-cache", icon: Database, label: "Entity Cache" },
  { id: "connectors", path: "/connectors", icon: Plugs, label: "Connectors" },
  { id: "rpa", path: "/rpa", icon: DesktopTower, label: "RPA Operations" },
  { id: "portal-desk", path: "/portal-desk", icon: DesktopTower, label: "Portal Desk" },
  { id: "portal-desk-v2", path: "/portal-desk-v2", icon: DesktopTower, label: "Portal Desk V2", badge: "Beta" },
  { id: "marketplace", path: "/marketplace", icon: Storefront, label: "Marketplace" },
  { id: "admin", path: "/admin/marketplace", icon: ShieldCheck, label: "Admin" },
  { id: "reports", path: "/reports", icon: ChartBar, label: "Reports" },
  { id: "api-docs", path: "/developer", icon: Code, label: "API Docs" },
  { id: "settings", path: "/settings", icon: Gear, label: "Settings" },
];

export default function Layout() {
  const supportBootstrap = useNativeSupportBootstrap();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "true");
  const [mode, setMode] = useState(() => localStorage.getItem("aw_shell_mode") || "operate");
  const [user, setUser] = useState(supportBootstrap?.auth?.user || null);
  const [pinnedWorkspaces, setPinnedWorkspaces] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { resolvedTheme, setTheme } = useTheme();

  useEffect(() => {
    if (supportBootstrap?.auth?.user) return;
    let current = true;
    getCurrentUser().then(res => { if (current) setUser(res.data); }).catch(() => {});
    return () => { current = false; };
  }, [supportBootstrap?.auth?.user]);
  useEffect(() => {
    setPinnedWorkspaces([]);
    if (mode !== "operate" || collapsed) return;
    const controller = new AbortController();
    let requestVersion = 0;
    const loadPinnedWorkspaces = () => {
      const version = ++requestVersion;
      setPinnedWorkspaces([]);
      return getPinnedSkillWorkspaces({ signal: controller.signal })
        .then((response) => {
          if (!controller.signal.aborted && version === requestVersion) {
            setPinnedWorkspaces((response.data.items || []).filter((item) => item.pinned).slice(0, 8));
          }
        })
        .catch(() => { if (!controller.signal.aborted && version === requestVersion) setPinnedWorkspaces([]); });
    };
    loadPinnedWorkspaces();
    window.addEventListener(SKILL_WORKSPACES_CHANGED_EVENT, loadPinnedWorkspaces);
    window.addEventListener(BROWSER_TENANT_CHANGED_EVENT, loadPinnedWorkspaces);
    return () => {
      controller.abort();
      window.removeEventListener(SKILL_WORKSPACES_CHANGED_EVENT, loadPinnedWorkspaces);
      window.removeEventListener(BROWSER_TENANT_CHANGED_EVENT, loadPinnedWorkspaces);
    };
  }, [mode, collapsed]);

  useEffect(() => { localStorage.setItem("sidebar_collapsed", collapsed); }, [collapsed]);
  useEffect(() => { localStorage.setItem("aw_shell_mode", mode); }, [mode]);

  useEffect(() => {
    const isOperatePath = ["/", "/entities", "/fleet", "/functions", "/briefings", "/activity", "/canvases", "/skill-workspaces"].some((path) => path === "/" ? location.pathname === "/" : location.pathname.startsWith(path));
    if (isOperatePath && mode !== "operate") setMode("operate");
    if (!isOperatePath && mode !== "manage") setMode("manage");
  }, [location.pathname, mode]);

  const handleLogout = async () => {
    if (isSupportTab()) {
      try { await supportRequest('/close', { close: true }); }
      finally { endSupportTab(); }
      return;
    }
    try { const { logout } = await import("../lib/api"); await logout(); } catch {}
    localStorage.removeItem('session_token');
    localStorage.removeItem(BROWSER_TENANT_STORAGE_KEY);
    navigate('/login', { replace: true });
  };

  const navItems = (mode === "operate" ? operateNavItems : manageNavItems)
    .filter((item) => !isSupportTab() || item.id !== "admin");
  const sidebarW = collapsed ? "w-[60px]" : "w-60";
  const mainML = collapsed ? "md:ml-[60px]" : "md:ml-60";
  // Reading-oriented routes keep a comfortable measure; every operational surface uses the
  // full shell width so dense rows gain columns instead of margins as resolution grows.
  const usesReadingWidth = ["/settings"].some((path) => location.pathname.startsWith(path));
  // Full-screen workspaces size themselves to the viewport; shell padding would push them past it.
  const usesFullBleed = ["/portal-desk"].some((path) => location.pathname.startsWith(path));

  const NavItem = ({ item, showLabel = true }) => {
    const { path, icon: Icon, label, id, badge } = item;
    // A whole path segment, so "/portal-desk" is not active on "/portal-desk-v2".
    const isActive = location.pathname === path || (path !== "/" && location.pathname.startsWith(`${path}/`));
    return (
      <div className="group relative flex items-center">
        <NavLink
          to={path}
          end={path === "/"}
          onClick={() => setMobileOpen(false)}
          className={`flex items-center gap-3 rounded-lg transition-colors flex-1 ${
            collapsed ? "px-0 py-2 justify-center" : "px-3 py-2"
          } ${isActive ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:text-foreground hover:bg-accent"}`}
          data-testid={`nav-${id}`}
          title={collapsed ? (badge ? `${label} (${badge})` : label) : undefined}
        >
          <Icon size={collapsed ? 20 : 18} weight={isActive ? "fill" : "regular"} />
          {showLabel && !collapsed && <span className="text-sm truncate">{label}</span>}
          {showLabel && !collapsed && badge && (
            <Badge variant="info" className="ml-auto min-h-0 px-1.5 py-px text-[10px]">{badge}</Badge>
          )}
        </NavLink>
        {/* Tooltip for collapsed mode */}
        {collapsed && (
          <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md border border-border shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
            {badge ? `${label} (${badge})` : label}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile toggle */}
      <Button variant="ghost" size="icon" className="fixed top-4 left-4 z-50 md:hidden" style={isSupportTab() ? { top: 'calc(var(--aw-support-offset, 0px) + 1rem)' } : undefined} onClick={() => setMobileOpen(!mobileOpen)}>
        {mobileOpen ? <X size={20} /> : <List size={20} />}
      </Button>

      {/* Sidebar */}
      <aside style={isSupportTab() ? { top: 'var(--aw-support-offset, 0px)', height: 'calc(100% - var(--aw-support-offset, 0px))' } : undefined} className={`fixed left-0 top-0 h-full ${sidebarW} border-r border-border bg-card/80 backdrop-blur-xl flex flex-col z-40 transition-all duration-200 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}>
        {/* Brand */}
        <div className={`flex items-center ${collapsed ? "justify-center p-3" : "p-5 pb-4"}`}>
          {collapsed ? (
            <span className="text-lg font-bold text-primary">A</span>
          ) : (
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-foreground tracking-tight">
                ActionWeave<span className="text-primary">.ai</span>
              </h1>
              <p className="mt-0.5 text-micro text-content-tertiary">Intelligent Workflow Automation</p>
            </div>
          )}
        </div>

        {!collapsed && (
          <div className="mx-3 mb-3 grid grid-cols-2 rounded-lg bg-muted p-1" role="tablist" aria-label="ActionWeave mode">
            {[
              ["operate", "Operate"],
              ["manage", "Manage"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={mode === value}
                onClick={() => {
                  setMode(value);
                  navigate(value === "operate" ? "/" : "/manage/dashboard");
                }}
                className={`rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${mode === value ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Mode-scoped items */}
        <nav className={`flex-1 ${collapsed ? "px-1.5" : "px-3"} overflow-y-auto space-y-0.5`}>
          {navItems.map(item => <NavItem key={item.id} item={item} />)}
          {mode === "operate" && pinnedWorkspaces.length > 0 && !collapsed && <div className="mt-3 border-t border-border pt-2"><p className="px-3 pb-1 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">Pinned Skills</p>{pinnedWorkspaces.map((item) => <NavItem key={`pinned-${item.skill_id}`} item={{ id: `pinned-${item.skill_id}`, path: item.path, icon: Sparkle, label: item.name }} />)}</div>}
        </nav>

        {/* Footer */}
        <div className={`border-t border-border ${collapsed ? "p-1.5" : "p-3"} space-y-1.5`}>
          {/* Collapse toggle */}
          <button
            onClick={() => setCollapsed(c => !c)}
            className={`flex items-center gap-2 w-full rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-accent transition-colors ${collapsed ? "justify-center py-2" : "px-3 py-1.5"}`}
            data-testid="collapse-sidebar"
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? <CaretRight size={14} /> : <><CaretLeft size={14} /><span>Collapse</span></>}
          </button>

          {/* Theme toggle */}
          <button
            onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
            className={`flex items-center gap-2 w-full rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-accent transition-colors ${collapsed ? "justify-center py-2" : "px-3 py-1.5"}`}
            data-testid="theme-toggle"
            title={resolvedTheme === "dark" ? "Light Mode" : "Dark Mode"}
          >
            {resolvedTheme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
            {!collapsed && (resolvedTheme === "dark" ? "Light Mode" : "Dark Mode")}
          </button>

          {/* User */}
          {user && (
            <div className={`flex items-center ${collapsed ? "justify-center py-1" : "gap-2 px-2"}`}>
              {user.picture ? (
                <img src={user.picture} alt="" className="h-7 w-7 rounded-full shrink-0" />
              ) : (
                <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0" title={collapsed ? user.name : undefined}>
                  <UserCircle size={16} className="text-primary" />
                </div>
              )}
              {!collapsed && (
                <>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground truncate">{user.name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{user.email}</p>
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0" onClick={handleLogout} data-testid="logout-btn">
                    <SignOut size={14} />
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      {/* min-w-0: a flex item defaults to min-width:auto, so any descendant that sets
          white-space:nowrap -- which every `truncate` does -- pushes this wider than the row
          instead of being clipped, and the whole page scrolls sideways. The Happening now list on
          Today was doing exactly that, at 3390px inside a 1500px viewport. */}
      <main className={`min-w-0 flex-1 ${mainML} transition-all duration-200`}>
        <div className={usesFullBleed ? "w-full min-w-0" : usesReadingWidth ? "mx-auto w-full max-w-[1400px] p-6 md:p-8" : "w-full min-w-0 p-3 sm:p-4 xl:px-6 xl:py-5 2xl:px-8"}>
          <Outlet />
        </div>
      </main>

      {/* Floating Mic */}
      {!isSupportTab() && <FloatingMic />}
    </div>
  );
}
